import * as React from "react";
import { connect } from "react-redux";
import { useCookies } from "react-cookie";
import { ToastContainer } from "react-toastify";
import { Route, Routes, useNavigate } from "react-router-dom";
import { BloodGlucoseListScreen } from "../BloodGlucose/BloodGlucoseListScreen";
import { CoughSessionsListScreen } from "../cough/SessionsList/CoughSessionsListScreen";
import { MultiStudyBrickwall } from "../misc/components/MultiStudyBrickwall";
import { MainState } from "./reducers";
import { DevicesScreen } from "../Devices/DevicesScreen";
import { UploadFileModal } from "../Devices/UploadRecordingFileModal";
import DataCorrectionRequestForm from "../DataCorrectionFacility/Request/DataCorrectionRequestForm";
import { NoStudyBrickwall } from "../misc/components/NoStudyBrickwall";
import ReportsApp from "../reports/ReportsApp";
import ReportViewerScreen from "../reports/ReportViewerScreen";
import { User } from "../misc/api/apiTypes";
import { AlertNotificationList } from '../alert-notification-list/AlertNotificationList';
import AdminMenuLayout from "../Administration/_AdminMenuLayout";
import { AuditTrailScreen } from "../AuditTrail/AuditTrailScreen";
import { FixedCommentsProvider } from './FixedCommentsContext';
import { SpirometryRoot } from "../PulmonaryFunctionTests/Spirometry/SpirometryRoot";
import { SvcRoot } from "../PulmonaryFunctionTests/Svc/SvcRoot";
import { DlcoRoot } from "../PulmonaryFunctionTests/Dlco/DlcoRoot";
import { SubjectsScreen } from "../Subjects/SubjectsScreen";
import { ReferenceContextProvider } from "./ReferenceContextProvider";
import { CurrentStudyConfigurationContextProvider } from "./CurrentStudyConfigurationContext";
import { SecurityContextProvider } from "./SecurityContext";
import { DiaryRoot } from "../EDiary/DiaryRoot";
import { Unauthorized } from "../errors/unauthorized/Unauthorized";
import { DirectoryVisualizer } from "../directory-visualizer/DirectoryVisualizer";
import { SelectStudy } from "../SelectStudy";
import { Dashboard } from '../dashboard/components/dashboard';
import { SupportDocuments } from "../Support/SupportDocuments";
import { SupportContactDetails } from "../Support/SupportContactDetails";
import { DataCorrectionListAndViewScreen } from "../DataCorrectionFacility/DataCorrectionListAndViewScreen";
import { SessionsListScreen } from "../Sessions/SessionsListScreen";
import { BloodGlucoseTestsScreen } from "../BloodGlucose/BloodGlucoseTestsScreen";
import { Study } from "webportalclient";
import { UnderApproval } from "../Account/UnderApproval";
import { TopNavbar } from "./TopNavbar";
import { AccountSettings } from "../Account/AccountSettings";
import { NoUser } from "../Account/NoUser";
import { LockedOut } from "../Account/LockedOut";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { SignedOut } from "../Account/SignedOut";
import { SignIn } from "../Account/SignIn";
import { GetStudyAction, LoadCurrentUserAction } from "./SecurityActions";
import { CoughUploadReport } from "../cough/UploadReport/CoughUploadReport";
import { PefRoot } from "../PulmonaryFunctionTests/Pef/PefRoot";
import { SurveyView } from "../Surveys/SurveyView";
import { MepRoot } from "../PulmonaryFunctionTests/Pressure/MEP/MepRoot";
import { MipRoot } from "../PulmonaryFunctionTests/Pressure/MIP/MipRoot";
import { SnipRoot } from "../PulmonaryFunctionTests/Pressure/SNIP/SnipRoot";
import { LungVolumeRoot } from "../PulmonaryFunctionTests/LungVolume/LungVolumeRoot";
import { OscillometryRoot } from "../PulmonaryFunctionTests/Oscillometry/OscillometryRoot";
import { EcgRoot } from "../Ecg/EcgRoot";
import { PrivateRoute, PrivateRouteAllStudies } from "./PrivateRoute";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { AdminHome } from "../Administration/AdminHome";
import { AccessRequestListScreen } from "../Administration/AccessRequests/AccessRequestListScreen";
import { CreateUserScreen } from "../Users/Form/CreateUserScreen";
import { EditUserScreen } from "../Users/Form/EditUserScreen";
import { AssociateUsersWithSites } from "../Users/AssociateUsersWithSites";
import { RolesRoot } from "../Administration/Roles/RolesRoot";
import { VisitSchedule } from "../Administration/VisitSchedule/VisitSchedule";
import { DiaryConfigurationsList } from "../Administration/Diary/DiaryConfigurationsList";
import { CreateDiaryConfigurationScreen } from "../Administration/Diary/CreateDiaryConfiguration";
import { EditDiaryConfigurationScreen } from "../Administration/Diary/EditDiaryConfiguration";
import { DiaryConfigurationFiles } from "../Administration/Diary/DiaryConfigurationFiles";
import FixedCommentsScreen from "../Administration/FixedComments/Index";
import CreateFixedCommentSetScreen from "../Administration/FixedComments/CreateFixedCommentSet/CreateFixedCommentSet";
import EditFixedCommentSetScreen from "../Administration/FixedComments/EditFixedCommentSet/EditFixedCommentSet";
import { DevicesList } from "../Administration/Devices/DevicesList";
import { CreateDeviceScreen } from "../Administration/Devices/CreateDevice";
import { EditDeviceScreen } from "../Administration/Devices/EditDevice";
import { UpdatePackagesList } from "../Administration/UpdatePackages/UpdatePackagesList";
import { StudiesScreen } from "../Administration/Studies/StudiesScreen";
import { SiteScreen } from "../Administration/Sites/SitesScreen";
import { SupportContactScreen } from "../Administration/SupportContact/SupportContactScreen";
import { ServiceResourcesList } from "../Administration/ServiceResources/ServiceResourcesList";
import { NotificationsPanel } from "../notifications/NotificationsPanel";
import { CustomCalculationsScreen } from "../Administration/Studies/CustomCalculationsScreen";
import { UserListWrapper } from "../Users/List/UserListWrapper";

export interface CoreAppProps {
    currentUser: User;
    currentStudy: Study;
    isNoStudies: boolean;
    version: string;
    logoutRedirectUri: string;

    dispatch: any;
}

export const CoreApp = (props: CoreAppProps) => {
    const [cookies] = useCookies(['WpCurrentStudy']);
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const [currentUser, setCurrentUser] = React.useState<User>();
    const [isMultiStudy, setIsMultiStudy] = React.useState<boolean>();
    const [isNoStudies, setIsNoStudies] = React.useState<boolean>();
    const [activeStudy, setActiveStudy] = React.useState<Study>();
    const [studyChange, setStudyChange] = React.useState<string>();
    const [isAllStudies, setIsAllStudies] = React.useState<boolean>(false);
    const navigate = useNavigate();

    React.useEffect(() => {
        const init = async () => {
            const currentStudyId = cookies.WpCurrentStudy;
            try {
                const userResult = await legacyClient.getCurrentUser();
                setCurrentUser(userResult);
                props.dispatch(LoadCurrentUserAction.create(userResult));   // Legacy study access support

                const studiesResult = await client.client.getStudies(undefined, undefined, undefined, undefined, 0, undefined, undefined, true);

                if (studiesResult["@odata.count"] === 0) {
                    setIsMultiStudy(true);
                    setIsNoStudies(true);
                    return;
                }

                const currentStudy = currentStudyId ? await (client.client.getStudy(currentStudyId).catch(() => undefined)) : undefined;

                if (!currentStudy)
                    setIsMultiStudy(true);
                else
                    setIsMultiStudy(false);

                setActiveStudy(currentStudy);
                props.dispatch(GetStudyAction.create(currentStudy?.id)); // Legacy study access support
            } catch (error) {
                if (error === "User requires approval")
                    navigate("/underapproval");
                else
                    navigate("/unauthorized");
            }
        }

        if (studyChange == "All Studies") {
            setIsMultiStudy(true);
            setIsNoStudies(true);
            setIsAllStudies(true)
            setActiveStudy(null);
            props.dispatch(GetStudyAction.create(null)); // Legacy study access support
            return;
        }

        init();
    }, [studyChange]);

    if (currentUser) {
        if (isMultiStudy) {
            return (
                <SecurityContextProvider user={currentUser}>
                    <ReferenceContextProvider version={props.version} logoutRedirectUri={props.logoutRedirectUri}>
                        <TopNavbar isMultiStudy={isMultiStudy} currentStudy={activeStudy} />
                        <ToastContainer />
                        <Routes>
                            <Route path="*" element={isAllStudies ? <MultiStudyBrickwall /> : <SelectStudy setStudyChange={setStudyChange} />} />
                            <Route path="/Audit/*" element={<AuditTrailScreen />} />
                            <Route path="/account/study" element={<SelectStudy setStudyChange={setStudyChange} />} />
                            <Route element={<AdminMenuLayout />}>
                                <Route path="users/create" element={<CreateUserScreen />} />
                                <Route path="users/edit/:id" element={<EditUserScreen />} />
                                <Route path="users/user-sites" element={<AssociateUsersWithSites />} />
                                <Route path="/Users/:didEmailFail?/:isSaveMessageDisplayed?" element={<UserListWrapper />} />
                                <Route path="/EDiaryConfigurations/*" element={<AdminMenuLayout/>} />
                                <Route
                                    path="/admin"
                                    element={
                                        <PrivateRouteAllStudies permissions={[
                                            PermissionEnum.AccessAdmin
                                        ]}>
                                            <AdminHome />
                                        </PrivateRouteAllStudies>
                                    }
                                />
                                <Route path="studies/*" element={<StudiesScreen />} />
                                <Route path="/requests" element={<AccessRequestListScreen />} />
                                <Route path="/underapproval/*" element={<UnderApproval logoutRedirectUri={props.logoutRedirectUri} />} />
                                <Route path="/account/settings" element={<AccountSettings />} />
                                <Route path="/cough/uploadreport/:reportId" element={<CoughUploadReport />} />
                            </Route>
                        </Routes>
                    </ReferenceContextProvider>
                </SecurityContextProvider>
            );
        } else if (activeStudy) {
            return (
                <SecurityContextProvider user={currentUser}>
                    <ReferenceContextProvider version={props.version} logoutRedirectUri={props.logoutRedirectUri}>
                        <CurrentStudyConfigurationContextProvider currentStudy={activeStudy}>
                            <FixedCommentsProvider currentStudy={activeStudy}>
                                <TopNavbar isMultiStudy={false} currentStudy={activeStudy} />
                                <ToastContainer />
                                <Routes>
                                    <Route path="/" element={<Dashboard />} />
                                    <Route path="/Subjects/*" element={<SubjectsScreen />} />
                                    <Route path="/bloodglucose/tests/*"
                                        element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.AccessAllBloodGlucoseTests
                                            ]}>
                                                <BloodGlucoseTestsScreen />
                                            </PrivateRoute>
                                        }
                                    />
                                    <Route path="/bloodglucose"
                                        element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.AccessAllBloodGlucoseSessions
                                            ]}>
                                                <BloodGlucoseListScreen />
                                            </PrivateRoute>
                                        }
                                    />
                                    <Route path="ecg/*" element={<EcgRoot />} />
                                    <Route path="/cough/uploadreport/:reportId" element={<CoughUploadReport />} />
                                    <Route path="/Cough/*" element={<CoughSessionsListScreen/>} />
                                    <Route path="/Devices" element={<DevicesScreen />} />
                                    <Route path="/upload"
                                        element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.UploadRecordingFile
                                            ]}>
                                                <UploadFileModal />
                                            </PrivateRoute>
                                        }
                                    />
                                    <Route path="/Dcf/Request" element={
                                        <PrivateRoute permissions={[PermissionEnum.RequestDCF]}>
                                            <DataCorrectionRequestForm />
                                        </PrivateRoute>
                                    }
                                    />
                                    <Route path="/dcf/*" element={
                                        <PrivateRoute permissions={[PermissionEnum.AccessDataCorrections]}>
                                            <DataCorrectionListAndViewScreen />
                                        </PrivateRoute>
                                    }
                                    />
                                    <Route path="/Spirometry/*" element={<SpirometryRoot/>} />
                                    <Route path="/Svc/*" element={<SvcRoot/>} />
                                    <Route path="/Dlco/*" element={<DlcoRoot/>} />
                                    <Route path="/LungVolume/*" element={<LungVolumeRoot/>} />
                                    <Route path="/Mip/*" element={<MipRoot/>} />
                                    <Route path="/Mep/*" element={<MepRoot/>} />
                                    <Route path="/Snip/*" element={<SnipRoot/>} />
                                    <Route path="/Pef/*" element={<PefRoot />} />
                                    <Route path="/Alerts"
                                    element={
                                        <PrivateRoute permissions={[
                                            PermissionEnum.ViewAlertNotifications
                                        ]}>
                                            <AlertNotificationList />
                                        </PrivateRoute>
                                    }
                                    />
                                    <Route path="/StudySettings" Component={AdminMenuLayout} />
                                    <Route path="/Reports" element={<PrivateRoute permissions={[PermissionEnum.ViewReports]}>
                                        <ReportsApp />
                                    </PrivateRoute>
                                    }
                                    />
                                    <Route path="/Reports/Viewer/:path" element={<PrivateRoute permissions={[PermissionEnum.ViewReports]}>
                                        <ReportViewerScreen />
                                    </PrivateRoute>
                                    }
                                    />
                                    <Route path="/Diary/*"
                                     element={
                                        <PrivateRoute permissions={[
                                            PermissionEnum.ViewAllDiarySessions
                                        ]}>
                                            <DiaryRoot />
                                        </PrivateRoute>
                                    }
                                    />
                                    <Route path="/sessions"
                                        element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.ViewSessionsList
                                            ]}>
                                                <SessionsListScreen />
                                            </PrivateRoute>
                                        }
                                    />
                                    <Route path="/Studies/FvcSettings/:id" Component={AdminMenuLayout} />
                                    <Route path="/Studies/DiarySettings/:id" Component={AdminMenuLayout} />
                                    <Route path="/Studies/MipSettings/:id" Component={AdminMenuLayout} />
                                    <Route path="/unauthorized" element={<Unauthorized />} />
                                    <Route path="/sharedfiles"
                                        element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.ViewMappedServerDirectories
                                            ]}>
                                                <DirectoryVisualizer />
                                            </PrivateRoute>
                                        }
                                    />
                                    <Route path="/underapproval" element={<UnderApproval logoutRedirectUri={props.logoutRedirectUri} />} />
                                    <Route path="/support/documents"
                                        element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.AddFile,
                                                PermissionEnum.DeleteFile
                                            ]}>
                                                <SupportDocuments />
                                            </PrivateRoute>
                                        }
                                    />
                                    <Route path="/support/details" element={<SupportContactDetails />} />
                                    <Route path="/Audit/*" element={<AuditTrailScreen />} />
                                    <Route path="/Oscillometry/*" element={<OscillometryRoot/>} />
                                    <Route path="/Surveys/:id" element={<SurveyView />} />
                                    <Route path="/account/study" element={<SelectStudy setStudyChange={setStudyChange} />} />
                                    <Route path="/account/settings" element={<AccountSettings />} />
                                    <Route
                                        element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.AccessAdmin
                                            ]}>
                                                <AdminMenuLayout />
                                            </PrivateRoute>
                                        }
                                    >
                                        <Route path="/admin" element={<AdminHome />} />
                                        <Route path="requests"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.ViewUserManagement
                                                ]}>
                                                    <AccessRequestListScreen />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="users/create"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.CreateUsers
                                                ]}>
                                                    <CreateUserScreen />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="users/edit/:id"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.EditUsers
                                                ]}>
                                                    <EditUserScreen />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="users/user-sites"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.AssociateUsersWithSites
                                                ]}>
                                                    <AssociateUsersWithSites />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="/users/:didEmailFail?/:isSaveMessageDisplayed?" element={<UserListWrapper />} />
                                        <Route path="rolemanagement/*" 
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.ViewRoleManagement
                                                ]}>
                                                    <RolesRoot />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="VisitSchedule/:studyId" element={<VisitSchedule />} />
                                        <Route path="DiaryConfigurations"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.ViewEDiaryConfigurations
                                                ]}>
                                                    <DiaryConfigurationsList />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="DiaryConfigurations/Create"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.CreateEDiaryConfiguration
                                                ]}>
                                                    <CreateDiaryConfigurationScreen />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="DiaryConfigurations/Edit/:id"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.EditEDiaryConfiguration
                                                ]}>
                                                    <EditDiaryConfigurationScreen />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="DiaryConfigurations/Files/:configId"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.ViewEDiaryConfigurations
                                                ]}>
                                                    <DiaryConfigurationFiles />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="FixedComments" element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.ViewFixedComments
                                            ]}>
                                                <FixedCommentsScreen />
                                            </PrivateRoute>
                                        }
                                        />
                                        <Route path="FixedComments/Create" element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.CreateFixedComment
                                            ]}>
                                                <CreateFixedCommentSetScreen />
                                            </PrivateRoute>
                                        }
                                        />
                                        <Route path="FixedComments/Edit/:id" element={
                                            <PrivateRoute permissions={[
                                                PermissionEnum.EditFixedComment
                                            ]}>
                                                <EditFixedCommentSetScreen />
                                            </PrivateRoute>
                                        }
                                        />
                                        <Route
                                            path="deviceadministration"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.ViewDevices
                                                ]}>
                                                    <DevicesList />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route
                                            path="deviceadministration/create"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.CreateDeviceResources
                                                ]}>
                                                    <CreateDeviceScreen />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route
                                            path="deviceadministration/edit/:id"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.EditDeviceResources
                                                ]}>
                                                    <EditDeviceScreen />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="UpdatePackages" element={<UpdatePackagesList />} />
                                        <Route path="studies/*" element={<StudiesScreen />} />
                                        <Route path="sites/*" element={<SiteScreen />} />
                                        <Route path="/serviceresources/*" element={<ServiceResourcesList/>} />
                                        <Route
                                            path="/admin/editcontact"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.EditSupportContactInformationAdministration
                                                ]}>
                                                    <SupportContactScreen />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/notifications"
                                            element={
                                                <PrivateRoute permissions={[
                                                    PermissionEnum.ViewNotificationSubscriptions
                                                ]}>
                                                    <NotificationsPanel />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="/admin/customcalculations" element={<CustomCalculationsScreen />} />
                                    </Route>
                                </Routes>
                            </FixedCommentsProvider>
                        </CurrentStudyConfigurationContextProvider>
                    </ReferenceContextProvider>
                </SecurityContextProvider>
            );
        }
    }
    if (isNoStudies) {
        return <NoStudyBrickwall />;
    }

    return (
        <Routes>
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/underapproval" element={<UnderApproval logoutRedirectUri={props.logoutRedirectUri} />} />
            <Route path="/account/nouser" element={<NoUser />} />
            <Route path="/account/lockedout" element={<LockedOut />} />
            <Route path="/account/signedout" element={<SignedOut />} />
            <Route element={<SignIn/>} />
        </Routes>
    );
}



function mapStateToProps(state: MainState) {
    return {
        currentStudy: state.core.currentStudy,
        currentUser: state.security.currentUser,
        isNoStudies: state.security.isNoStudies
    };
}

export default connect(mapStateToProps)(CoreApp);
